/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as MoreIcon } from 'app/assets/Icons/More.svg';
import { Copy } from '@zurich-data-dsm-icons/action.copy';
import { Trash } from '@zurich-data-dsm-icons/action.trash';
import { Pencil } from '@zurich-data-dsm-icons/action.pencil';
import { ReactComponent as FolderIcon } from 'app/assets/Icons/Folder.svg';
import { ReactComponent as DownloadIcon } from 'app/assets/Icons/Download.svg';
import { ReactComponent as UploadIcon } from 'app/assets/Icons/AttachWhite.svg';
import { ReactComponent as MoveIcon } from 'app/assets/Icons/DocumentForward.svg';
import { ReactComponent as TranslateIcon } from 'app/assets/Icons/Translate.svg';
import { ReactComponent as CollapseIcon } from 'app/assets/Icons/Arrows.svg';
import styles from './OptionsButton.module.scss';
import { handleFileSelection } from 'app/utils';
import { SERVICE_NAMES } from 'app/utils/consts';
import { Tooltip } from 'react-tooltip';
import { IOptionsButton } from './OptionsButton.interface';

export const OptionsButton: React.FC<IOptionsButton> = ({
  variant,
  onEdit,
  onDelete,
  onUpload,
  onCreateFolder,
  onDownload,
  onCopy,
  onMove,
  onTranslate,
  onCollapse
}) => {
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsToolbarVisible(false);
      }
    };

    if (isToolbarVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isToolbarVisible]);

  const toggleToolbar = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsToolbarVisible(!isToolbarVisible);
  };

  const handleAction = (action?: () => void): void => {
    if (action) {
      action();
    }
    setIsToolbarVisible(false);
  };

  const handleUpload = (): void => {
    handleFileSelection(
      (e) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length > 0 && onUpload) {
          onUpload(files);
        }
      },
      SERVICE_NAMES.ASK_YOUR_DOCS,
      true
    );
  };

  const getActiveFeatures = (): string[] => {
    switch (variant) {
      case 'chat':
        return ['rename', 'delete'];
      case 'document':
        return ['download', 'delete', 'translate', 'rename'];
      case 'folder':
        return ['upload', 'rename', 'delete'];
      case 'folder-with-selection':
        return ['upload', 'rename', 'delete', 'move', 'copy'];
      case 'section':
        return ['upload', 'create-folder', 'collapse'];
      case 'section-with-selection':
        return ['delete', 'upload', 'create-folder', 'collapse'];
      default:
        return [];
    }
  };

  const activeFeatures = getActiveFeatures();

  return (
    <div ref={containerRef} className={styles['options-container']}>
      <Button
        aria-label="Options"
        variant="icon"
        size="small"
        className={styles.options}
        onClick={toggleToolbar}
      >
        <Button.Icon>
          <MoreIcon />
        </Button.Icon>
      </Button>

      {isToolbarVisible && (
        <div
          className={styles.toolbar}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
            }
          }}
          tabIndex={-1}
        >
          {activeFeatures.includes('rename') && (
            <Button
              variant="icon"
              aria-label="Rename"
              className={styles.action}
              onClick={() => handleAction(onEdit)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Rename"
            >
              <Button.Icon>
                <Pencil />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('delete') && (
            <Button
              variant="icon"
              aria-label="Delete"
              className={styles.action}
              onClick={() => handleAction(onDelete)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Delete"
            >
              <Button.Icon>
                <Trash />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('upload') && (
            <Button
              variant="icon"
              aria-label="Upload"
              className={styles.action}
              onClick={handleUpload}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Upload files"
            >
              <Button.Icon>
                <UploadIcon />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('create-folder') && (
            <Button
              variant="icon"
              aria-label="Create Folder"
              className={styles.action}
              onClick={() => handleAction(onCreateFolder)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Create folder"
            >
              <Button.Icon>
                <FolderIcon />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('download') && (
            <Button
              variant="icon"
              aria-label="Download"
              className={styles.action}
              onClick={() => handleAction(onDownload)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Download searchable PDF"
            >
              <Button.Icon>
                <DownloadIcon />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('copy') && (
            <Button
              variant="icon"
              aria-label="Copy Document"
              className={styles.action}
              onClick={() => handleAction(onCopy)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Copy to this folder"
            >
              <Button.Icon>
                <Copy />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('move') && (
            <Button
              variant="icon"
              aria-label="Move Document"
              className={styles.action}
              onClick={() => handleAction(onMove)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Move to this folder"
            >
              <Button.Icon>
                <MoveIcon />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('collapse') && (
            <Button
              variant="icon"
              aria-label="Collapse Folders"
              className={styles.action}
              onClick={() => handleAction(onCollapse)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Collapse folders"
            >
              <Button.Icon>
                <CollapseIcon />
              </Button.Icon>
            </Button>
          )}

          {activeFeatures.includes('translate') && (
            <Button
              variant="icon"
              aria-label="Translate Document"
              className={styles.action}
              onClick={() => handleAction(onTranslate)}
              data-tooltip-id="option-tooltip"
              data-tooltip-content="Translate document"
            >
              <Button.Icon>
                <TranslateIcon />
              </Button.Icon>
            </Button>
          )}
          <Tooltip id="option-tooltip" className={styles['options-tooltip']} />
        </div>
      )}
    </div>
  );
};
